/**
 * The messages between the page and the KDF worker (decision 25; part 25a log, table M). The page sends one derivation:
 * the password, the account's KDF record and the highest parameters seen for it; the worker answers once, with the login
 * secret and the highest to remember, a refusal, or a failure. Each side reads what it receives field by field
 * (Definition of Done, security), so a message that isn't one of these is never taken for one. Part 41b adds the backup
 * lock's two jobs: a backup key for a passphrase, and a locked backup opened.
 */
import { LOGIN_SECRET_BYTES, PASSWORD_WRAPPING_BYTES, readKdfRecord } from '@cockatrice/crypto';
import type { BackupFailure, BackupKey, KdfParameters, KdfRecord, ParameterRefusal } from '@cockatrice/crypto';
import { readBackupKey, readCheckValue } from '../backup/lock.ts';

/** M1: a derivation of the login secret. */
export interface KdfJob {
  readonly kind: 'derive';
  readonly password: string;
  readonly record: KdfRecord;
  readonly highest: KdfParameters | null;
}

/** Why a derivation was refused: the record's parameters (table K), or a password that can't be hashed (table P). */
export type KdfRefusal = ParameterRefusal | 'password';

/** M2–M4: the worker's answer (part 38c: with the password's wrapping key, GP3). */
export type KdfAnswer =
  | { readonly kind: 'derived'; readonly secret: Uint8Array; readonly wrapping: Uint8Array; readonly highest: KdfParameters }
  | { readonly kind: 'refused'; readonly reason: KdfRefusal }
  | { readonly kind: 'failed' };

/** The refusals, as sent. */
const REFUSALS: readonly KdfRefusal[] = ['below-floor', 'above-ceiling', 'below-highest', 'password'];

/** Whether `value` is a plain object with exactly `fields`. */
function hasFields(value: unknown, fields: readonly string[]): value is object {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const keys = Object.keys(value).sort();
  const wanted = [...fields].sort();
  return keys.length === wanted.length && keys.every((key, i) => key === wanted[i]);
}

/** A whole number of at least one. */
function isCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 1;
}

/** M5: parameters (memory and passes, whole and positive), or null for anything else. */
function readParameters(value: unknown): KdfParameters | null {
  if (!hasFields(value, ['memoryKiB', 'passes'])) return null;
  const memoryKiB: unknown = Reflect.get(value, 'memoryKiB');
  const passes: unknown = Reflect.get(value, 'passes');
  return isCount(memoryKiB) && isCount(passes) ? { memoryKiB, passes } : null;
}

/** M1, M5: reads a job in the worker; null for anything that isn't one. */
export function readJob(data: unknown): KdfJob | null {
  if (!hasFields(data, ['kind', 'password', 'record', 'highest'])) return null;
  const password: unknown = Reflect.get(data, 'password');
  const record = readKdfRecord(Reflect.get(data, 'record'));
  const given: unknown = Reflect.get(data, 'highest');
  const highest = given === null ? null : readParameters(given);
  if (Reflect.get(data, 'kind') !== 'derive' || typeof password !== 'string' || record === null || (given !== null && highest === null)) return null;
  return { kind: 'derive', password, record, highest };
}

/** M2–M5: reads the worker's answer on the page; null for anything that isn't one. */
export function readAnswer(data: unknown): KdfAnswer | null {
  if (hasFields(data, ['kind']) && Reflect.get(data, 'kind') === 'failed') return { kind: 'failed' };
  if (hasFields(data, ['kind', 'reason'])) {
    const given: unknown = Reflect.get(data, 'reason');
    const reason = REFUSALS.find((refusal) => refusal === given);
    return Reflect.get(data, 'kind') === 'refused' && reason !== undefined ? { kind: 'refused', reason } : null;
  }
  if (!hasFields(data, ['kind', 'secret', 'wrapping', 'highest']) || Reflect.get(data, 'kind') !== 'derived') return null;
  const secret: unknown = Reflect.get(data, 'secret');
  const wrapping: unknown = Reflect.get(data, 'wrapping');
  const highest = readParameters(Reflect.get(data, 'highest'));
  if (!(secret instanceof Uint8Array) || secret.length !== LOGIN_SECRET_BYTES || !(wrapping instanceof Uint8Array) || wrapping.length !== PASSWORD_WRAPPING_BYTES || highest === null) return null;
  return { kind: 'derived', secret, wrapping, highest };
}

/**
 * Part 41b (BP2, E2, E15, E16): the backup key for a passphrase, derived off the page: with `kdf` (a check of the lock set)
 * or, when null, a new KDF record (set or changed: a fresh salt, `KDF_DEFAULT`).
 */
export interface BackupKeyJob {
  readonly kind: 'backup-key';
  readonly passphrase: string;
  readonly kdf: KdfRecord | null;
}

/** Part 41b (E11–E13): a locked backup file opened with a passphrase, off the page. */
export interface OpenBackupJob {
  readonly kind: 'open-backup';
  readonly file: Blob;
  readonly passphrase: string;
}

/** Part 41b: the backup lock's jobs. */
export type BackupKdfJob = BackupKeyJob | OpenBackupJob;

/** Part 41b: the worker's answer to a backup job: the key and its check value, the opened file, a refusal (E12, E13), or a failure. */
export type BackupKdfAnswer =
  | { readonly kind: 'backup-key'; readonly key: BackupKey; readonly check: Uint8Array<ArrayBuffer> }
  | { readonly kind: 'opened'; readonly file: Blob }
  | { readonly kind: 'backup-refused'; readonly reason: BackupFailure }
  | { readonly kind: 'failed' };

/** E12, E13: the refusals, as sent. */
const BACKUP_FAILURES: readonly BackupFailure[] = ['not-backup', 'newer', 'malformed', 'too-large', 'wrong-passphrase', 'damaged'];

/** Part 41b: reads a backup job in the worker; null for anything that isn't one. */
export function readBackupJob(data: unknown): BackupKdfJob | null {
  if (hasFields(data, ['kind', 'passphrase', 'kdf']) && Reflect.get(data, 'kind') === 'backup-key') {
    const passphrase: unknown = Reflect.get(data, 'passphrase');
    const given: unknown = Reflect.get(data, 'kdf');
    const kdf = given === null ? null : readKdfRecord(given);
    return typeof passphrase === 'string' && (given === null || kdf !== null) ? { kind: 'backup-key', passphrase, kdf } : null;
  }
  if (hasFields(data, ['kind', 'file', 'passphrase']) && Reflect.get(data, 'kind') === 'open-backup') {
    const file: unknown = Reflect.get(data, 'file');
    const passphrase: unknown = Reflect.get(data, 'passphrase');
    return file instanceof Blob && typeof passphrase === 'string' ? { kind: 'open-backup', file, passphrase } : null;
  }
  return null;
}

/** Part 41b: reads the worker's answer to a backup job on the page; null for anything that isn't one. */
export function readBackupAnswer(data: unknown): BackupKdfAnswer | null {
  if (hasFields(data, ['kind']) && Reflect.get(data, 'kind') === 'failed') return { kind: 'failed' };
  if (hasFields(data, ['kind', 'key', 'check']) && Reflect.get(data, 'kind') === 'backup-key') {
    const key = readBackupKey(Reflect.get(data, 'key'));
    const check = readCheckValue(Reflect.get(data, 'check'));
    return key !== null && check !== null ? { kind: 'backup-key', key, check } : null;
  }
  if (hasFields(data, ['kind', 'file']) && Reflect.get(data, 'kind') === 'opened') {
    const file: unknown = Reflect.get(data, 'file');
    return file instanceof Blob ? { kind: 'opened', file } : null;
  }
  if (hasFields(data, ['kind', 'reason']) && Reflect.get(data, 'kind') === 'backup-refused') {
    const given: unknown = Reflect.get(data, 'reason');
    const reason = BACKUP_FAILURES.find((failure) => failure === given);
    return reason === undefined ? null : { kind: 'backup-refused', reason };
  }
  return null;
}
